import Overlay from '@/app/components/Overlay'
import { Icons } from '@/app/components/ui/Icons'
import { LG_BREAKPOINT } from '@/app/constants'
import { emitEmailFieldClickGAEvent } from '@/app/ga-event-helpers'
import { SearchItem } from '@/app/types'
import { getDateLocaleByLangCode, isRtlLanguage, validateEmail } from '@/app/utils'
import { useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { format } from 'date-fns'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { FormEvent, useEffect, useState } from 'react'
import Button from './common/Button'
import Checkbox from './common/Checkbox'
import Input from './Input'

interface PriceAlertFormProps {
	destination: SearchItem
	startDate: Date
	endDate: Date
	isOpen: boolean
	emailInputError?: string
	guestCount?: number
	onSubmit?: (email: string) => void
	onChange?: (email: string) => void
}

const EmailSignup: React.FC<PriceAlertFormProps> = ({
	destination,
	startDate,
	endDate,
	isOpen,
	emailInputError,
	guestCount,
	onChange,
	onSubmit,
}) => {
	const [email, setEmail] = useState('')
	const [receiveDeals, setReceiveDeals] = useState(true)
	const [emailError, setEmailError] = useState('')
	// const isMd = useMediaQuery({ query: MD_BREAKPOINT })
	const tabletLGUp = useMediaQuery(LG_BREAKPOINT)
	const lng = usePathname().split('/')[1] || 'en'
	const isRtl = isRtlLanguage(lng)

	const t = useTranslations('Form')
	const tc = useTranslations('ContactUs')

	useEffect(() => {
		if (emailInputError) {
			setEmailError(emailInputError)
		}
	}, [emailInputError])

	const handleSubmit = (e?: FormEvent) => {
		e?.preventDefault()

		if (email.length && !validateEmail(email)) {
			setEmailError(t('emailError'))
			return
		}
		setEmailError('')
		onSubmit && onSubmit(email)
	}

	const dayFormat = lng === 'vi' ? 'd MMM' : 'MMM d';
	const formattedDate = `${format(startDate, dayFormat, {
		locale: getDateLocaleByLangCode(lng),
	})} - ${format(endDate, dayFormat, { locale: getDateLocaleByLangCode(lng) })}`

	const handleInput = (value: string) => {
		setEmail(value)
		onChange && onChange(value)
		setEmailError('')
	}

	const handleFocus = () => {
		emitEmailFieldClickGAEvent()
	}

	return tabletLGUp ? (
		<>
			<Overlay
				isOpened={isOpen}
				onClick={handleSubmit}
				openInput={() => null}
			/>
			<div
				className="email-signup email-signup--desktop"
				data-test-id="email-signup-modal"
			>
				<div className="email-signup__header flex relative justify-center mb-4">
					<Image
						src={'/images/email-bell.png'}
						alt={'bell icon'}
						width={66}
						height={66}
						quality={100}
						className="email-signup__icon"
						unoptimized
					/>
					<div className="email-signup__close" onClick={(e) => handleSubmit(e)}>
						<Icons.iconClose className="email-signup__close-icon" />
					</div>
				</div>
				<h2 className="email-signup__title">{t('getPriceAlert')}</h2>
				<div className="email-signup__destination-info ">
					<div className="email-signup__destination-name">
						{destination.name}, {destination.country}
					</div>
					<div className="email-signup__destination-details">
						{formattedDate}
						<div className="email-signup__separator"></div>
						{guestCount} {t('guests')}
					</div>
				</div>
				<form onSubmit={handleSubmit} className="email-signup__form">
					<div className="email-signup__input-wrapper">
						<Input
							className={classNames(
								'email-signup__input',
								{
									'email-signup__input--error border-error': emailError,
								},
								'sentry-mask'
							)}
							placeholder={t('emailPlaceholder')}
							value={email}
							onChange={(value) => handleInput(value)}
							id={'email-signup-input'}
							onFocus={handleFocus}
						>
							<Icons.letter stroke={emailError ? '#D61F69' : ''} />
						</Input>
						{emailError && (
							<div className="email-signup__error">{emailError}</div>
						)}
					</div>
					<div className="email-signup__button-wrapper">
						<Button
							id="email-signup-button"
							type="submit"
							name={'Sign up button'}
							textColor="white"
							big
							className="email-signup__button"
						>
							<Icons.iconLens className="email-signup__button-icon" />
							{t('signupAndSearch')}
						</Button>
					</div>
					<div className="email-signup__checkbox-wrapper">
						<Checkbox
							checked={receiveDeals}
							className="email-signup__checkbox"
							onChange={(checked) => setReceiveDeals(checked)}
							labelRight={t('signupCheckboxLabel')}
							testId="receive-deals-checkbox"
							variant="main"
							bold={false}
						/>
						<p className="email-signup__terms">
							{t.rich('emailTerms', {
								termsLink: (chunks) => (
									<Link
										href={`/terms`}
										className="email-signup__link underline"
										target="_blank"
									>
										{chunks}
									</Link>
								),
								privacyPolicy: (chunks) => (
									<Link
										href={`/privacy-policy`}
										className="email-signup__link underline"
										target="_blank"
									>
										{chunks}
									</Link>
								),
							})}
						</p>
					</div>
				</form>
			</div>
		</>
	) : (
		<div className="email-signup email-signup--mobile">
			<Input
				className={classNames(
					'email-signup__input',
					{
						'email-signup__input--error border-error': emailError,
					},
					'sentry-mask'
				)}
				placeholder={t('emailPlaceholder')}
				value={email}
				onChange={(value) => handleInput(value)}
				id={'email-signup-input'}
				onFocus={handleFocus}
			>
				<Icons.letter stroke={emailError ? '#D61F69' : ''} />
			</Input>
			{emailError && <div className="email-signup__error">{emailError}</div>}
		</div>
	)
}

export default dynamic(() => Promise.resolve(EmailSignup), { ssr: false })
